import type { Document, ProductType } from '@/types'

type DocumentListener = (documents: Document[]) => void

const DOCUMENTS_JSON_PATH = '/documents.json'
const LOCAL_OVERRIDE_KEY = 'nexgen:documents'

interface GetDocumentsOptions {
  includeInactive?: boolean
}

class DocumentService {
  private documents: Document[] = []
  private loadingPromise: Promise<Document[]> | null = null
  private listeners = new Set<DocumentListener>()

  private get isBrowser(): boolean {
    return typeof window !== 'undefined' && typeof localStorage !== 'undefined'
  }

  private getLocalOverride(): Document[] | null {
    if (!this.isBrowser) return null
    const raw = localStorage.getItem(LOCAL_OVERRIDE_KEY)
    if (!raw) return null
    try {
      const parsed = JSON.parse(raw)
      if (Array.isArray(parsed)) {
        return parsed.map((doc, index) => this.normalizeDocument(doc, index))
      }
    } catch (error) {
      console.error('Failed to parse local document override', error)
    }
    return null
  }

  private persistLocal(documents: Document[]): void {
    if (!this.isBrowser) return
    try {
      localStorage.setItem(LOCAL_OVERRIDE_KEY, JSON.stringify(documents))
    } catch (error) {
      console.error('Failed to persist documents locally', error)
    }
  }

  private async fetchRemoteDocuments(): Promise<Document[]> {
    const response = await fetch(`${DOCUMENTS_JSON_PATH}?v=${Date.now()}`)
    if (!response.ok) {
      throw new Error(`Failed to load documents.json (${response.status})`)
    }
    const data = await response.json()
    const documents = Array.isArray(data) ? data : data.documents
    if (!Array.isArray(documents)) {
      throw new Error('documents.json is not formatted correctly')
    }
    return documents.map((doc: Partial<Document>, index: number) =>
      this.normalizeDocument(doc, index),
    )
  }

  private normalizeDocument(doc: Partial<Document>, index = 0): Document {
    const productType: ProductType =
      doc.productType === 'underlayment' ? 'underlayment' : 'structural-floor'
    const fallbackFilename =
      doc.filename || this.extractFilenameFromUrl(doc.url) || `document-${index + 1}.pdf`
    const numericSortOrder = Number(doc.sortOrder)
    const sortOrder =
      Number.isFinite(numericSortOrder) && numericSortOrder > 0
        ? numericSortOrder
        : index + 1

    return {
      id: doc.id || `doc-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
      name: doc.name || 'Untitled Document',
      description: doc.description || '',
      filename: fallbackFilename,
      url: doc.url || '',
      size: doc.size,
      type: doc.type || 'TDS',
      required: doc.required ?? false,
      products:
        doc.products ||
        (productType === 'structural-floor'
          ? ['3/4-in (20mm)']
          : ['1/2-in (13mm)', '5/8-in (16mm)']),
      productType,
      sortOrder,
      active: doc.active ?? true,
    }
  }

  private extractFilenameFromUrl(url?: string): string | null {
    if (!url) return null
    try {
      const parsed = new URL(url, typeof window !== 'undefined' ? window.location.origin : undefined)
      const segments = parsed.pathname.split('/')
      const candidate = segments.pop()
      return candidate || null
    } catch {
      const segments = url.split('/')
      return segments.pop() || null
    }
  }

  private async ensureLoaded(): Promise<Document[]> {
    if (this.documents.length) {
      return this.documents
    }

    if (this.loadingPromise) {
      return this.loadingPromise
    }

    this.loadingPromise = (async () => {
      const override = this.getLocalOverride()
      if (override && override.length) {
        this.documents = override
        return this.documents
      }

      const remote = await this.fetchRemoteDocuments()
      this.documents = remote
      return this.documents
    })()

    try {
      return await this.loadingPromise
    } finally {
      this.loadingPromise = null
      this.notifyListeners()
    }
  }

  private notifyListeners(): void {
    const snapshot = this.documents.map(doc => ({ ...doc }))
    this.listeners.forEach(listener => listener(snapshot))
  }

  subscribe(listener: DocumentListener): () => void {
    this.listeners.add(listener)
    if (this.documents.length) {
      listener(this.documents.map(doc => ({ ...doc })))
    }
    return () => this.listeners.delete(listener)
  }

  async getAllDocuments(options: GetDocumentsOptions = {}): Promise<Document[]> {
    const docs = await this.ensureLoaded()
    const filtered = options.includeInactive ? docs : docs.filter(doc => doc.active)
    return [...filtered].sort((a, b) => a.sortOrder - b.sortOrder)
  }

  async getDocumentsByProductType(
    productType: ProductType,
    options: GetDocumentsOptions = {},
  ): Promise<Document[]> {
    const docs = await this.getAllDocuments(options)
    return docs.filter(doc => doc.productType === productType)
  }

  async createDocument(input: Partial<Document>): Promise<Document> {
    if (!input.name || !input.url || !input.type) {
      throw new Error('Document name, type, and URL are required')
    }
    const docs = await this.ensureLoaded()
    const productType: ProductType =
      input.productType === 'underlayment' ? 'underlayment' : 'structural-floor'
    const desiredSortOrder = Number(input.sortOrder)
    const fallbackSortOrder =
      Math.max(
        0,
        ...docs.filter(doc => doc.productType === productType).map(doc => doc.sortOrder),
      ) + 1
    const nextSortOrder =
      Number.isFinite(desiredSortOrder) && desiredSortOrder > 0 ? desiredSortOrder : fallbackSortOrder

    const document = this.normalizeDocument(
      {
        ...input,
        productType,
        sortOrder: nextSortOrder,
      },
      docs.length,
    )

    this.documents = [...docs, document]
    this.persistLocal(this.documents)
    this.notifyListeners()
    return document
  }

  async updateDocument(id: string, updates: Partial<Document>): Promise<Document> {
    const docs = await this.ensureLoaded()
    const index = docs.findIndex(doc => doc.id === id)
    if (index === -1) {
      throw new Error('Document not found')
    }

    const updated = this.normalizeDocument(
      {
        ...docs[index],
        ...updates,
        id,
      },
      index,
    )

    this.documents = [
      ...docs.slice(0, index),
      updated,
      ...docs.slice(index + 1),
    ]
    this.persistLocal(this.documents)
    this.notifyListeners()
    return updated
  }

  async deleteDocument(id: string): Promise<void> {
    const docs = await this.ensureLoaded()
    this.documents = docs.filter(doc => doc.id !== id)
    this.persistLocal(this.documents)
    this.notifyListeners()
  }

  async reloadFromRemote(): Promise<void> {
    this.documents = await this.fetchRemoteDocuments()
    this.persistLocal(this.documents)
    this.notifyListeners()
  }

  async exportDocuments(): Promise<void> {
    if (!this.isBrowser) return
    const docs = await this.ensureLoaded()
    const payload = { documents: docs }
    const blob = new Blob([JSON.stringify(payload, null, 2)], { type: 'application/json' })
    const url = URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.download = 'documents.json'
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    URL.revokeObjectURL(url)
  }

  clearLocalCache(): void {
    if (!this.isBrowser) return
    localStorage.removeItem(LOCAL_OVERRIDE_KEY)
  }

}

export const documentService = new DocumentService()

